/** Where a newly inline-added activity lands on the board. Pure, so the
 *  date/placement rule is unit-tested without React.
 *
 *  - WBS leaf/row: the row the activity was added under (its anchor); a caller-
 *    supplied fallback otherwise (the focused leaf / the first leaf).
 *  - Start column: the workday right AFTER the latest predecessor's current
 *    board finish (Finish→Start, no overlap); the data date when it has no
 *    resolvable predecessor. Clamped into the calendar so the bar always lands
 *    on-window. */

export interface LeafInfo {
  key: string;
  path: string[];
  nodeId: number;
}

export interface InlineAddPlacement {
  wbsKey: string;
  wbsPath: string[];
  wbsNodeId: number;
  startIndex: number;
}

export interface InlineAddPlacementParams {
  /** Predecessor graph ids — the new activity starts after the latest one. */
  predIds: string[];
  /** The activity this row was typed beneath — the leaf-row anchor. */
  anchorId: string | null;
  durationDays: number;
  /** gid → its WBS leaf (the anchor's row; also a just-added activity's row so a
   *  chained add inherits the same level). */
  leafOfGid: (id: string) => LeafInfo | null;
  /** gid → its current board finish (ISO); null for ties outside this board. */
  boardFinishOf: (id: string) => string | null;
  calendarIndexFor: (iso: string | null) => number;
  calendarLength: number;
  dataDate: string | null;
  /** Leaf to use when the anchor resolves to none (focused / first leaf). */
  fallbackLeaf: LeafInfo | null;
}

const NO_LEAF: LeafInfo = { key: "(no WBS node)", path: ["(no WBS node)"], nodeId: 0 };

export function computeInlineAddPlacement(
  params: InlineAddPlacementParams,
): InlineAddPlacement {
  const duration = Math.max(1, Math.round(params.durationDays) || 1);

  const leaf =
    (params.anchorId != null ? params.leafOfGid(params.anchorId) : null) ??
    params.fallbackLeaf ??
    NO_LEAF;

  // Start the workday AFTER the latest predecessor's board finish; fall back to
  // the data date when no predecessor places it.
  let startCol = -1;
  for (const pid of params.predIds) {
    const col = params.calendarIndexFor(params.boardFinishOf(pid));
    if (col >= 0) startCol = Math.max(startCol, col + 1);
  }
  if (startCol < 0) startCol = params.calendarIndexFor(params.dataDate);
  if (startCol < 0) startCol = 0;

  // Keep the whole bar on the calendar (a far-out start would otherwise clip).
  const maxStart = Math.max(0, params.calendarLength - duration);
  const startIndex = Math.min(Math.max(0, startCol), maxStart);

  return { wbsKey: leaf.key, wbsPath: leaf.path, wbsNodeId: leaf.nodeId, startIndex };
}
